import { Injectable, computed } from '@angular/core';
import { patchState, signalState } from '@ngrx/signals';

import { TaxParams, IncomeTaxParams, BUNDESLAENDER, Steuerklasse, KrankenversicherungsArt } from '../../models/tax-params.model';
import { BruttoNettoResult } from '../../models/brutto-netto-result.model';

export interface BruttoNettoState {
  // User inputs
  bruttoMonthly: number;
  bruttoYearlyInput: number | null; // original yearly input to avoid round-trip errors
  steuerklasse: Steuerklasse;
  kirchensteuer: boolean;
  bundesland: string;
  kinderlos: boolean;
  anzahlKinder: number;
  kinderfreibetraege: number;
  kvZusatzbeitrag: number;
  year: number;

  // Geldwerter Vorteil (benefit in kind)
  geldwerterVorteil: number; // Monthly non-cash benefit (e.g. company car) — increases SV/tax basis but not cash payout

  // Insurance options
  gesetzlichRentenversichert: boolean;
  gesetzlichArbeitslosenversichert: boolean;
  krankenversicherungsArt: KrankenversicherungsArt;
  privatKvBeitrag: number; // Monthly PKV premium (employee share), only when privat

  // Loaded data
  taxParams: TaxParams | null;
}

export const initialBruttoNettoState: BruttoNettoState = {
  bruttoMonthly: 0,
  bruttoYearlyInput: null,
  steuerklasse: 1,
  kirchensteuer: false,
  bundesland: 'nordrhein-westfalen',
  kinderlos: true,
  anzahlKinder: 0,
  kinderfreibetraege: 0,
  kvZusatzbeitrag: 0.029,
  year: new Date().getFullYear(),
  geldwerterVorteil: 0,
  gesetzlichRentenversichert: true,
  gesetzlichArbeitslosenversichert: true,
  krankenversicherungsArt: 'gesetzlich',
  privatKvBeitrag: 0,
  taxParams: null
};

@Injectable({ providedIn: 'root' })
export class BruttoNettoCalculatorService {
  state = signalState<BruttoNettoState>(initialBruttoNettoState);

  updateState(partial: Partial<BruttoNettoState>) {
    patchState(this.state, partial);
  }

  // ─── Assessment Basis ─────────────────────────────────────────────

  /** Monthly SV/tax assessment basis: brutto + geldwerter Vorteil */
  assessmentBasis = computed(() => this.state.bruttoMonthly() + this.state.geldwerterVorteil());

  // ─── Social Security Computed Signals ─────────────────────────────

  /** Monthly Rentenversicherung (pension) employee contribution */
  rentenversicherung = computed(() => {
    const s = this.state;
    const params = s.taxParams();
    if (!params || s.bruttoMonthly() <= 0 || !s.gesetzlichRentenversichert()) return 0;
    const rv = params.socialSecurity.rentenversicherung;
    const assessable = Math.min(this.assessmentBasis(), rv.bbgMonthly);
    return this.round2(assessable * rv.employeeRate);
  });

  /** Monthly Arbeitslosenversicherung (unemployment) employee contribution */
  arbeitslosenversicherung = computed(() => {
    const s = this.state;
    const params = s.taxParams();
    if (!params || s.bruttoMonthly() <= 0 || !s.gesetzlichArbeitslosenversichert()) return 0;
    const alv = params.socialSecurity.arbeitslosenversicherung;
    const assessable = Math.min(this.assessmentBasis(), alv.bbgMonthly);
    return this.round2(assessable * alv.employeeRate);
  });

  /** Monthly Krankenversicherung (health) employee contribution incl. Zusatzbeitrag */
  krankenversicherung = computed(() => {
    const s = this.state;
    const params = s.taxParams();
    if (!params || s.bruttoMonthly() <= 0) return 0;

    // Privat krankenversichert: fixed monthly premium
    if (s.krankenversicherungsArt() === 'privat') {
      return this.round2(s.privatKvBeitrag());
    }

    // Gesetzlich or freiwillig: same rate-based calculation
    const kv = params.socialSecurity.krankenversicherung;
    const assessable = Math.min(this.assessmentBasis(), kv.bbgMonthly);
    // Employee pays: general rate / 2 (= employeeRate) + Zusatzbeitrag / 2
    const totalRate = kv.employeeRate + s.kvZusatzbeitrag() / 2;
    return this.round2(assessable * totalRate);
  });

  /** Monthly Pflegeversicherung (care) employee contribution */
  pflegeversicherung = computed(() => {
    const s = this.state;
    const params = s.taxParams();
    if (!params || s.bruttoMonthly() <= 0) return 0;

    // Privat krankenversichert: PV is included in the PKV premium
    if (s.krankenversicherungsArt() === 'privat') return 0;

    const pv = params.socialSecurity.pflegeversicherung;
    const assessable = Math.min(this.assessmentBasis(), pv.bbgMonthly);
    let rate = pv.employeeRate;

    // Sachsen: Arbeitnehmer zahlen 0,5 % mehr (Buß- und Bettag)
    const bundesland = BUNDESLAENDER.find(b => b.key === s.bundesland());
    if (bundesland) {
      rate += bundesland.pvMehrbeitrag;
    }

    if (s.kinderlos()) {
      // Childless surcharge (for employees 23+)
      rate += pv.childlessSurcharge;
    } else if (s.anzahlKinder() >= 2) {
      // Discount for 2nd–5th child under 25
      const discountChildren = Math.min(s.anzahlKinder() - 1, pv.maxChildDiscountChildren - 1);
      rate -= discountChildren * pv.childDiscount;
      rate = Math.max(rate, 0);
    }

    return this.round2(assessable * rate);
  });

  /** Total monthly social security contributions */
  socialSecurityTotal = computed(() => {
    return this.round2(this.rentenversicherung() + this.arbeitslosenversicherung() + this.krankenversicherung() + this.pflegeversicherung());
  });

  // ─── Tax Computed Signals ─────────────────────────────────────────

  /** Monthly Lohnsteuer (income tax) */
  lohnsteuer = computed(() => {
    const s = this.state;
    const params = s.taxParams();
    if (!params || s.bruttoMonthly() <= 0) return 0;

    const annualGross = this.assessmentBasis() * 12;
    const zvE = this.calculateZvE(annualGross, params, s.steuerklasse(), s.anzahlKinder());
    const annualTax = this.calculateAnnualTax(zvE, s.steuerklasse(), params.incomeTax);

    return this.round2(annualTax / 12);
  });

  /** Monthly Solidaritätszuschlag */
  solidaritaetszuschlag = computed(() => {
    const s = this.state;
    const params = s.taxParams();
    if (!params || s.bruttoMonthly() <= 0) return 0;

    const annualGross = this.assessmentBasis() * 12;
    const zvE = this.calculateZvE(annualGross, params, s.steuerklasse(), s.anzahlKinder());

    // For Soli, Kinderfreibeträge reduce the reference tax
    const kfbReduction = s.kinderfreibetraege() * params.deductions.kinderfreibetragAmount;
    const zvEForSoli = Math.max(zvE - kfbReduction, 0);
    const annualTaxForSoli = this.calculateAnnualTax(zvEForSoli, s.steuerklasse(), params.incomeTax);

    const freigrenze = s.steuerklasse() === 3 ? params.solidaritaetszuschlag.freigrenzeStk3 : params.solidaritaetszuschlag.freigrenze;

    if (annualTaxForSoli <= freigrenze) return 0;

    const soliFromRate = annualTaxForSoli * params.solidaritaetszuschlag.rate;
    const soliFromMilderung = (annualTaxForSoli - freigrenze) * params.solidaritaetszuschlag.milderungszoneFactor;
    const annualSoli = Math.min(soliFromRate, soliFromMilderung);

    return this.round2(annualSoli / 12);
  });

  /** Monthly Kirchensteuer */
  kirchensteuer = computed(() => {
    const s = this.state;
    const params = s.taxParams();
    if (!params || s.bruttoMonthly() <= 0 || !s.kirchensteuer()) return 0;

    const annualGross = this.assessmentBasis() * 12;
    const zvE = this.calculateZvE(annualGross, params, s.steuerklasse(), s.anzahlKinder());

    // For KiSt, Kinderfreibeträge reduce the reference tax
    const kfbReduction = s.kinderfreibetraege() * params.deductions.kinderfreibetragAmount;
    const zvEForKiSt = Math.max(zvE - kfbReduction, 0);
    const annualTaxForKiSt = this.calculateAnnualTax(zvEForKiSt, s.steuerklasse(), params.incomeTax);

    const bundesland = BUNDESLAENDER.find(b => b.key === s.bundesland());
    const kiStRate = bundesland?.kirchensteuerRate ?? 0.09;

    return this.round2((annualTaxForKiSt * kiStRate) / 12);
  });

  /** Total monthly taxes */
  taxesTotal = computed(() => {
    return this.round2(this.lohnsteuer() + this.solidaritaetszuschlag() + this.kirchensteuer());
  });

  // ─── Result Computed Signals ──────────────────────────────────────

  /** Total monthly deductions */
  totalDeductions = computed(() => {
    return this.round2(this.socialSecurityTotal() + this.taxesTotal());
  });

  /** Net monthly salary */
  nettoMonthly = computed(() => {
    return this.round2(this.state.bruttoMonthly() - this.totalDeductions());
  });

  // ─── Yearly Computed Signals ──────────────────────────────────────

  bruttoYearly = computed(() => this.state.bruttoYearlyInput() ?? this.round2(this.state.bruttoMonthly() * 12));
  rentenversicherungYearly = computed(() => this.round2(this.rentenversicherung() * 12));
  arbeitslosenversicherungYearly = computed(() => this.round2(this.arbeitslosenversicherung() * 12));
  krankenversicherungYearly = computed(() => this.round2(this.krankenversicherung() * 12));
  pflegeversicherungYearly = computed(() => this.round2(this.pflegeversicherung() * 12));
  socialSecurityTotalYearly = computed(() => this.round2(this.socialSecurityTotal() * 12));
  lohnsteuerYearly = computed(() => this.round2(this.lohnsteuer() * 12));
  solidaritaetszuschlagYearly = computed(() => this.round2(this.solidaritaetszuschlag() * 12));
  kirchensteuerYearly = computed(() => this.round2(this.kirchensteuer() * 12));
  taxesTotalYearly = computed(() => this.round2(this.taxesTotal() * 12));
  totalDeductionsYearly = computed(() => this.round2(this.totalDeductions() * 12));
  nettoYearly = computed(() => this.round2(this.nettoMonthly() * 12));

  // ─── Shareable URL ─────────────────────────────────────────────────

  generatedUrl = computed(() => {
    const s = this.state;
    const params = [
      `year=${s.year()}`,
      `bruttoMonthly=${s.bruttoMonthly()}`,
      `steuerklasse=${s.steuerklasse()}`,
      `kirchensteuer=${s.kirchensteuer()}`,
      `bundesland=${s.bundesland()}`,
      `kinderlos=${s.kinderlos()}`,
      `anzahlKinder=${s.anzahlKinder()}`,
      `kinderfreibetraege=${s.kinderfreibetraege()}`,
      `kvZusatzbeitrag=${Math.round(s.kvZusatzbeitrag() * 10000) / 10000}`,
      `gesetzlichRentenversichert=${s.gesetzlichRentenversichert()}`,
      `gesetzlichArbeitslosenversichert=${s.gesetzlichArbeitslosenversichert()}`,
      `krankenversicherungsArt=${s.krankenversicherungsArt()}`,
      `privatKvBeitrag=${s.privatKvBeitrag()}`,
      `geldwerterVorteil=${s.geldwerterVorteil()}`
    ];
    return encodeURI(`https://www.metallgehalt.de/brutto-netto-rechner?${params.join('&')}`);
  });

  /** Complete result object */
  result = computed(
    (): BruttoNettoResult => ({
      bruttoMonthly: this.state.bruttoMonthly(),
      rentenversicherung: this.rentenversicherung(),
      arbeitslosenversicherung: this.arbeitslosenversicherung(),
      krankenversicherung: this.krankenversicherung(),
      pflegeversicherung: this.pflegeversicherung(),
      socialSecurityTotal: this.socialSecurityTotal(),
      lohnsteuer: this.lohnsteuer(),
      solidaritaetszuschlag: this.solidaritaetszuschlag(),
      kirchensteuer: this.kirchensteuer(),
      taxesTotal: this.taxesTotal(),
      totalDeductions: this.totalDeductions(),
      nettoMonthly: this.nettoMonthly()
    })
  );

  // ─── Private Calculation Methods ──────────────────────────────────

  /**
   * Calculate the zu versteuerndes Einkommen (taxable income) for a given annual gross.
   * Reads insurance status from state to adjust Vorsorgepauschale accordingly.
   */
  private calculateZvE(annualGross: number, params: TaxParams, steuerklasse: Steuerklasse, anzahlKinder: number): number {
    const s = this.state;
    const monthlyBrutto = annualGross / 12;

    // Teilbetrag 1: Rentenversicherung (only if gesetzlich versichert)
    let annualRV = 0;
    if (s.gesetzlichRentenversichert()) {
      annualRV = Math.min(monthlyBrutto, params.socialSecurity.rentenversicherung.bbgMonthly) * params.socialSecurity.rentenversicherung.employeeRate * 12;
    }

    // Teilbetrag 3 (VSP3): Actual KV/PV employee contributions for Vorsorgepauschale
    let annualKV = 0;
    let annualPV = 0;
    if (s.krankenversicherungsArt() === 'privat') {
      // For PKV: use the employee's PKV premium as KV basis for Vorsorgepauschale
      annualKV = s.privatKvBeitrag() * 12;
      annualPV = 0; // PV included in PKV premium
    } else {
      // KV for VP: PAP uses ermäßigter Beitragssatz / 2 = 0.07 (not allgemeiner 0.073) + Zusatzbeitrag AN share
      const kvRate = 0.07 + s.kvZusatzbeitrag() / 2;
      annualKV = Math.min(monthlyBrutto, params.socialSecurity.krankenversicherung.bbgMonthly) * kvRate * 12;
      // PV: actual employee rate incl. Sachsen Mehrbeitrag + kinderlos/child adjustments (PAP: VPVB + VPVS)
      const bundesland = BUNDESLAENDER.find(b => b.key === s.bundesland());
      let pvRate = params.socialSecurity.pflegeversicherung.employeeRate + (bundesland?.pvMehrbeitrag ?? 0);
      if (s.kinderlos()) {
        pvRate += params.socialSecurity.pflegeversicherung.childlessSurcharge;
      } else if (s.anzahlKinder() >= 2) {
        const discountChildren = Math.min(s.anzahlKinder() - 1, params.socialSecurity.pflegeversicherung.maxChildDiscountChildren - 1);
        pvRate -= discountChildren * params.socialSecurity.pflegeversicherung.childDiscount;
        pvRate = Math.max(pvRate, 0);
      }
      annualPV = Math.min(monthlyBrutto, params.socialSecurity.pflegeversicherung.bbgMonthly) * pvRate * 12;
    }

    // Vorsorgepauschale (PAP UPVSP): Teilbetrag 1 (RV) + max(Teilbetrag 2 minimum, Teilbetrag 3 actual)
    const rvPauschale = annualRV;
    const kvPvActual = annualKV + annualPV; // VSP3: tatsächliche Beiträge
    const kvPvMinimum = Math.min(annualGross * 0.12, steuerklasse === 3 ? 3000 : 1900); // VSP2: Mindestvorsorgepauschale (12% of gross, capped at 1900/3000)
    const vorsorgepauschale = Math.ceil(rvPauschale + Math.max(kvPvMinimum, kvPvActual));

    let zvE = annualGross;

    // Steuerklasse-specific deductions
    switch (steuerklasse) {
      case 1:
      case 4:
        zvE -= params.deductions.werbungskostenpauschale;
        zvE -= params.deductions.sonderausgabenpauschale;
        zvE -= vorsorgepauschale;
        break;
      case 2:
        zvE -= params.deductions.werbungskostenpauschale;
        zvE -= params.deductions.sonderausgabenpauschale;
        zvE -= vorsorgepauschale;
        zvE -= params.deductions.entlastungsbetragAlleinerziehende;
        // Additional per child beyond the first
        if (anzahlKinder > 1) {
          zvE -= (anzahlKinder - 1) * params.deductions.entlastungsbetragProWeiteresKind;
        }
        break;
      case 3:
        zvE -= params.deductions.werbungskostenpauschale;
        zvE -= params.deductions.sonderausgabenpauschale;
        zvE -= vorsorgepauschale;
        break;
      case 5:
        zvE -= params.deductions.werbungskostenpauschale;
        zvE -= params.deductions.sonderausgabenpauschale;
        zvE -= vorsorgepauschale;
        break;
      case 6:
        // Steuerklasse 6: Only Vorsorgepauschale, no other deductions
        zvE -= vorsorgepauschale;
        break;
    }

    return Math.max(Math.floor(zvE), 0);
  }

  /**
   * Calculate annual income tax based on Steuerklasse.
   */
  private calculateAnnualTax(zvE: number, steuerklasse: Steuerklasse, params: IncomeTaxParams): number {
    if (zvE <= 0) return 0;

    switch (steuerklasse) {
      case 1:
      case 2:
      case 4:
        return this.applyTaxFormula(zvE, params);
      case 3:
        // Splittingtabelle: tax on half income, then doubled
        return 2 * this.applyTaxFormula(Math.floor(zvE / 2), params);
      case 5:
        // No Grundfreibetrag: shift zvE up by Grundfreibetrag to cancel Zone 1
        return this.applyTaxFormula(zvE + params.grundfreibetrag, params);
      case 6:
        // No Grundfreibetrag either
        return this.applyTaxFormula(zvE + params.grundfreibetrag, params);
      default:
        return 0;
    }
  }

  /**
   * Apply §32a EStG income tax formula (Grundtabelle).
   */
  private applyTaxFormula(zvE: number, p: IncomeTaxParams): number {
    if (zvE <= 0) return 0;

    // Zone 1: Tax-free up to Grundfreibetrag
    if (zvE <= p.grundfreibetrag) {
      return 0;
    }

    // Zone 2: Progressive 14%–24%
    if (zvE <= p.zone2End) {
      const y = (zvE - p.grundfreibetrag) / 10000;
      return Math.floor((p.zone2Factor1 * y + p.zone2Factor2) * y);
    }

    // Zone 3: Progressive 24%–42%
    if (zvE <= p.zone3End) {
      const z = (zvE - p.zone2End) / 10000;
      return Math.floor((p.zone3Factor1 * z + p.zone3Factor2) * z + p.zone3Constant);
    }

    // Zone 4: 42% (Spitzensteuersatz)
    if (zvE <= p.zone4End) {
      return Math.floor(p.zone4Rate * zvE - p.zone4Subtract);
    }

    // Zone 5: 45% (Reichensteuersatz)
    return Math.floor(p.zone5Rate * zvE - p.zone5Subtract);
  }

  /**
   * Round to 2 decimal places.
   */
  private round2(value: number): number {
    return Math.round(value * 100) / 100;
  }
}
